"use client";
import { useTranslations } from "next-intl";
import * as dc from "@/components/layout/list/defaultType";
import { Skeleton } from "@radix-ui/themes";
import { DataInputParams } from "./inputType";
import { Form, Input, Skeleton as AntSkeleton, Tooltip, Flex, Space } from "antd";
import { Debug } from "@/util/debug";
import { checkDisabled, checkNoItem, GetDataInputElement, getPlaceholder, getTooltip } from "./inputFunction";
import { LinkButton, CopyButton } from "./inputButton";
import { getParamsValue } from "@/util/paramutil";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { TransitionType } from "@/components/layout/layoutType";
import { cn } from "@/lib/utils";
import { defaultAllowClear } from "../list/defaultType";
const getInputCommon = (params: DataInputParams) => {
  return !params.password ? Input : Input.Password;
};
const getInput = (t: TransitionType, params: DataInputParams, setLinkValue: Dispatch<SetStateAction<any | undefined>>) => {
  const disabled = checkDisabled(params);
  const placeholder = getPlaceholder(t, params, disabled) ?? params.value ?? params.defaultValue; //params.placeholder ?? (params.inputMode == "view" ? "" : params.value ?? params.defaultValue);
  const unitPrev = params.unitPrev ? GetDataInputElement(params.unitPrev, false, params.values, t) : undefined;
  const unit = params.unit ? GetDataInputElement(params.unit, true, params.values, t) : undefined;
  //const disabled = params.inputMode == "read" ? true : params.disabled;
  const countDisabled = checkDisabled(params);
  const value = params.inputMode == "view" && !(params.readInputDisabled ?? false) ? (params.fixedValue ?? params.value) : undefined;
  const InputCommon = params.inputType === "inputSearch" ? Input.Search : !params.password ? Input : Input.Password;
  if (params.inputType === "textArea") {
    return (
      <Input.TextArea
        className={cn(
          params.inputClassName,
          params.hiddenCheckValue ? "item-hidden-check" : "",
          params.nextInputHidden ? "next-input-hidden" : "",
          (params.textAreaResize ?? true) ? "" : "resize-none",
        )}
        autoSize={params.textAreaAutoSize}
        style={params.inputStyle}
        variant={params.variant}
        placeholder={placeholder}
        value={value}
        //defaultValue={params.value ?? params.defaultValue}
        disabled={disabled}
        id={params.id}
        name={params.name}
        minLength={params.minLength !== undefined ? params.minLength : undefined}
        maxLength={params.maxLength !== undefined ? params.maxLength : undefined}
        onChange={params.onChange}
        allowClear={params.inputMode != "view" && (params.allowClear ?? true) ? defaultAllowClear : false}
        readOnly={params.readOnly ?? params.inputMode == "view"}
        autoComplete={params.autocomplete ? "on" : params.autocomplete === undefined ? "on" : "off"}
        showCount={!countDisabled && (params.showCount ?? false)}
        count={{ show: !countDisabled && (params.showCount ?? false), strategy: (txt) => Buffer.byteLength(txt) }}
        {...params.textAreaProp}
      />
    );
  } else {
    return (
      <InputCommon
        className={cn(
          params.inputClassName,
          params.hiddenCheckValue ? "item-hidden-check" : "",
          params.nextInputHidden ? "next-input-hidden" : "",
        )}
        style={params.inputStyle}
        variant={params.variant ?? (params.inputMode === "view" ? dc.viewVariant : undefined)}
        placeholder={placeholder}
        value={value}
        // defaultValue={params.value ?? params.defaultValue}
        disabled={disabled}
        prefix={unitPrev}
        suffix={unit}
        id={params.id}
        name={params.name}
        min={params.minValue !== undefined ? params.minValue : undefined}
        max={params.maxValue !== undefined ? params.maxValue : undefined}
        minLength={params.minLength !== undefined ? params.minLength : undefined}
        maxLength={params.maxLength !== undefined ? params.maxLength : undefined}
        step={params.step}
        onClick={
          params.onClick
            ? (e: any) => {
                if (params.onClick) {
                  params.onClick({ key: params.name, value: e.target.value, name: params.subListName, index: params.index, event: e });
                }
              }
            : undefined
        }
        onChange={
          params.inputType === "link" || params.onChange
            ? (e: any) => {
                if (params.inputType === "link") {
                  //Debug(e.target.value);
                  setLinkValue(e.target.value);
                }
                if (params.onChange) {
                  params.onChange({ key: params.name, value: e.target.value, name: params.subListName, index: params.index, event: e });
                }
              }
            : undefined
        }
        onPressEnter={params.onPressEnter}
        allowClear={params.inputMode != "view" && (params.allowClear ?? true) ? defaultAllowClear : false}
        readOnly={params.readOnly ?? params.inputMode == "view"}
        autoComplete={params.autocomplete ? "on" : params.autocomplete === undefined ? "on" : "off"}
        showCount={!countDisabled && (params.showCount ?? false)}
        count={{ show: !countDisabled && (params.showCount ?? false), strategy: (txt) => Buffer.byteLength(txt) }}
        {...params.searchProp}
      />
    );
  }

  // onDoubleClick={
  //   disabled
  //     ? async () => {
  //         try {
  //           await navigator.clipboard.writeText(params.value);
  //           alert("");
  //         } catch (e) {console.log(e)}
  //       }
  //     : undefined
  // }
};

export default function DataInput(params: DataInputParams) {
  const t = useTranslations(params.transitionGroup ?? "contents");
  const [linkValue, setLinkValue] = useState<any | undefined>(params.value ?? params.defaultValue);
  const [tooltip, setTooltip] = useState(getTooltip(t, params));

  const prev = params.prev ? GetDataInputElement(params.prev, false, params.values, t) : undefined;
  const next = params.next ? GetDataInputElement(params.next, true, params.values, t) : undefined;

  useEffect(() => {
    setTooltip(getTooltip(t, params));
  }, [t, params, params.tooltip]);

  if (params.formFunction && params.name) {
    if (!params.formFunction[params.name]) {
      params.formFunction[params.name] = {};
    }
    const temp_function = params.formFunction[params.name];
    if (temp_function) {
      if (params.inputType === "link") {
        temp_function["setLinkValue"] = setLinkValue;
      }
    }
  }

  return (
    <>
      <Tooltip color={dc.tooltipColor} title={tooltip} placement={params.tooltipPlacement} {...params.tooltipProps}>
        <Flex className={cn((params.widthFull ?? true) ? "w-full" : "")}>
          {params.inputType === "link" && (params.linkPropOption?.linkButtonFront ?? true) ? (
            <LinkButton {...params} value={linkValue} />
          ) : (
            ""
          )}
          {params.copyProp?.copyButton && (params.copyProp?.copyButtonFront ?? true) ? <CopyButton {...params} value={linkValue} /> : ""}
          <Space.Compact className={(params.widthFull ?? true) ? "w-full" : ""}>
            {prev}
            {checkNoItem(params) ? (
              getInput(t, params, setLinkValue)
            ) : (
              <Form.Item
                name={params.name}
                noStyle
                initialValue={params.fixedValue ?? params.value ?? params.defaultValue}
                required={params.required}
              >
                {getInput(t, params, setLinkValue)}
              </Form.Item>
            )}
            {next}
          </Space.Compact>
          {params.copyProp?.copyButton && !(params.copyProp?.copyButtonFront ?? true) ? <CopyButton {...params} value={linkValue} /> : ""}
          {params.inputType === "link" && !(params.linkPropOption?.linkButtonFront ?? true) ? (
            <LinkButton {...params} value={linkValue} />
          ) : (
            ""
          )}
        </Flex>
      </Tooltip>
    </>
  );
}
